import {
  FormField,
  FormLevel,
  generateLevelName,
  isTitleEligibleField,
} from './form-builder.utils';
import { createEmptyRootLevel } from './template-mapping';

/**
 * Zustand einer Designer-Sitzung: die bearbeitete Struktur, die aktuelle
 * Auswahl und die Operationen darauf (DOCU-1066).
 *
 * Bewusst ohne Angular — beide Oberflächen (Backoffice-Editor, AdminPanel)
 * hängen ihre Komponenten hier ein, statt die Regeln je einmal nachzubauen.
 * Die Builder-Komponenten mutieren `fields` teilweise selbst (das Canvas fügt
 * ein gezogenes Feld direkt ein); die Methoden hier sind entsprechend darauf
 * ausgelegt, danach aufgerufen zu werden.
 */
export class FormBuilderSession {
  rootLevel: FormLevel = createEmptyRootLevel();

  selectedField: FormField | null = null;
  selectedLevel: FormLevel | null = null;
  /** Rechte Spalte zeigt Ebenen- statt Feldkonfiguration. */
  isLevelSelected = false;

  hasUnsavedChanges = false;

  /** Ids aller Drop-Listen, damit zwischen Ebenen gezogen werden kann. */
  allDropListIds: string[] = [];

  constructor(rootLevel?: FormLevel) {
    if (rootLevel) this.load(rootLevel);
    else this.updateDropListIds();
  }

  load(rootLevel: FormLevel): void {
    this.rootLevel = rootLevel;
    this.clearSelection();
    this.hasUnsavedChanges = false;
    this.updateDropListIds();
  }

  // -- Felder ---------------------------------------------------------------

  /** Das Canvas hat das Feld bereits eingefügt; hier folgt der Rest. */
  onFieldAdded(event: { field: FormField; level: FormLevel }): void {
    this.hasUnsavedChanges = true;

    // DOCU-324: Titel und Untertitel für die ersten geeigneten Felder vorschlagen.
    if (isTitleEligibleField(event.field)) {
      if (!event.level.titleFieldId) {
        event.level.titleFieldId = event.field.id;
      } else if (!event.level.subtitleFieldId) {
        event.level.subtitleFieldId = event.field.id;
      }
    }

    this.selectField(event.field, event.level);
  }

  onFieldRemoved(event: { field: FormField; level: FormLevel }): void {
    const index = event.level.fields.findIndex((f) => f.id === event.field.id);
    if (index > -1) {
      event.level.fields.splice(index, 1);
      this.hasUnsavedChanges = true;
    }
    if (this.selectedField?.id === event.field.id) {
      this.selectedField = null;
      this.selectedLevel = null;
    }
  }

  onFieldsReordered(event: { fields: FormField[]; level: FormLevel }): void {
    event.level.fields = event.fields;
    this.hasUnsavedChanges = true;
  }

  onFieldConfigChanged(updatedField: FormField): void {
    if (!this.selectedLevel) return;
    const index = this.selectedLevel.fields.findIndex((f) => f.id === updatedField.id);
    if (index > -1) {
      this.selectedLevel.fields[index] = { ...updatedField };
      this.selectedField = this.selectedLevel.fields[index];
      this.hasUnsavedChanges = true;
    }
  }

  // -- Auswahl --------------------------------------------------------------

  selectField(field: FormField, level: FormLevel): void {
    this.selectedField = field;
    this.selectedLevel = level;
    this.isLevelSelected = false;
  }

  selectLevel(level: FormLevel): void {
    this.selectedField = null;
    this.selectedLevel = level;
    this.isLevelSelected = true;
  }

  clearSelection(): void {
    this.selectedField = null;
    this.selectedLevel = null;
    this.isLevelSelected = false;
  }

  // -- Ebenen ---------------------------------------------------------------

  onLevelConfigChanged(updatedLevel: FormLevel): void {
    if (!this.selectedLevel) return;
    this.selectedLevel.name = updatedLevel.name;
    this.selectedLevel.description = updatedLevel.description;
    this.selectedLevel.titleFieldId = updatedLevel.titleFieldId;
    this.selectedLevel.subtitleFieldId = updatedLevel.subtitleFieldId;
    // DOCU-664: DOCX-Export-Steuerung übernehmen
    this.selectedLevel.exportFormat = updatedLevel.exportFormat;
    this.selectedLevel.exportTableMarker = updatedLevel.exportTableMarker;
    this.hasUnsavedChanges = true;
  }

  addNestedLevel(parentLevel: FormLevel): FormLevel {
    const newLevel: FormLevel = {
      id: 'level_' + Date.now(),
      name: generateLevelName(parentLevel.children),
      description: '',
      fields: [],
      children: [],
      isExpanded: true,
    };
    parentLevel.children.push(newLevel);
    this.hasUnsavedChanges = true;
    this.updateDropListIds();
    this.selectLevel(newLevel);
    return newLevel;
  }

  removeNestedLevel(level: FormLevel, parent: FormLevel): void {
    const index = parent.children.findIndex((c) => c.id === level.id);
    if (index > -1) {
      parent.children.splice(index, 1);
      this.hasUnsavedChanges = true;
      this.updateDropListIds();
    }
  }

  duplicateNestedLevel(level: FormLevel, parent: FormLevel): void {
    const copy = this.deepCloneLevel(level);
    copy.name = level.name;

    const index = parent.children.findIndex((c) => c.id === level.id);
    if (index > -1) {
      parent.children.splice(index + 1, 0, copy);
    } else {
      parent.children.push(copy);
    }
    this.hasUnsavedChanges = true;
    this.updateDropListIds();
  }

  // -- Drop-Listen ----------------------------------------------------------

  updateDropListIds(): void {
    this.allDropListIds = this.collectDropListIds(this.rootLevel);
  }

  private collectDropListIds(level: FormLevel): string[] {
    const ids = ['level-' + level.id];
    level.children.forEach((child) => {
      ids.push(...this.collectDropListIds(child));
    });
    return ids;
  }

  private deepCloneLevel(level: FormLevel): FormLevel {
    return {
      id: 'level_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11),
      name: level.name,
      description: level.description,
      titleFieldId: level.titleFieldId,
      subtitleFieldId: level.subtitleFieldId,
      // DOCU-664: Export-Steuerung und unbekannte Properties mitklonen
      exportFormat: level.exportFormat,
      exportTableMarker: level.exportTableMarker,
      extra: level.extra ? { ...level.extra } : undefined,
      fields: level.fields.map((field, index) => ({
        ...field,
        // Eindeutige Id aus Zeitstempel, Zufall und Index — sonst kollidieren
        // die Felder einer im selben Millisekundentakt duplizierten Ebene.
        id: 'field_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11) + '_' + index,
      })),
      children: level.children.map((child) => this.deepCloneLevel(child)),
      isExpanded: level.isExpanded,
    };
  }
}
